import React, { useEffect, useState } from 'react';
import './Footer.scss';
import NewsletterSignUp from './NewsletterSignUp/NewsletterSignUp';
import FooterMenuList from './FooterMenuList/FooterMenuList';
import FooterContactList from './FooterContactList/FooterContactList';
import FooterSocialList from './FooterSocialList/FooterSocialList';
import Select from '../UI/Select/Select';
import FooterLegalList from './FooterLegalList/FooterLegalList';
import FooterOwl from './FooterOwl/FooterOwl';
import { getModuleBackgroundColor } from '../../util/getModuleBackgroundColor';
import { Waypoint } from 'react-waypoint';
import useMightyMouse from 'react-hook-mighty-mouse';

/**
 * Renders the global footer component
 * @param {string} newsletterSignupTitle - the title for the newsletter sign up
 * @param {string} companyMenuTitle - label for the company menu
 * @param {array} companyMenuList - links for the company menu
 * @param {string} contactMenuTitle - label for the contact menu
 * @param {array} contactMenuGroup1 - first group of links in the contact, denoted by a white dot
 * @param {array} contactMenuGroup2 - second group of links in the contact, denoted by a white dot
 * @param {array} contactMenuGroup3 - third group of links in the contact, denoted by a white dot
 * @param {string} resellersMenuTitle - label for the resellers menu
 * @param {array} resellersMenuList - links for the resellers menu
 * @param {array} socialLinks - links for the social list
 * @param {array} legalLinks - links for the legal list
 * @param {array} countriesData
 * @returns {JSX.Element}
 * @constructor
 */
const Footer = ({
  newsletterSignupTitle,
  companyMenuTitle,
  companyMenuList,
  contactMenuTitle,
  contactMenuGroup1,
  contactMenuGroup2,
  contactMenuGroup3,
  resellersMenuTitle,
  resellersMenuList,
  socialLinks,
  legalLinks,
  countriesData,
}) => {
  const [isHovering, setIsHovering] = useState(false);

  // init Beacon footer tracking variables
  let handleEnter;
  let handleExit;

  useEffect(() => {
    const beacon = document.querySelector('.c-beacon-button__button');

    // define the methods when the component mounts
    handleEnter = () => {
      beacon.classList.add('is-hidden');
    };

    handleExit = () => {
      beacon.classList.remove('is-hidden');
    };
  });

  const onChange = (e) => {
    e.preventDefault();
    const { value } = e.target;

    window.location = value;
  };

  const {
    selectedElement: {
      position: { angle: angleLeftEye },
    },
  } = useMightyMouse(false, 'c-footer-owl__left-eye', { x: 90, y: 90 });

  const {
    selectedElement: {
      position: { angle: angleRightEye },
    },
  } = useMightyMouse(false, 'c-footer-owl__right-eye', { x: 90, y: 90 });

  const reset =
    (angleLeftEye < 30 || (angleLeftEye < 360 && angleLeftEye > 330)) &&
    angleRightEye > 150 &&
    angleRightEye < 210;

  const rotateLeftEye = `rotate(${reset ? 0 : -angleLeftEye}deg)`;
  const rotateRightEye = `rotate(${reset ? 0 : -angleRightEye}deg)`;

  return (
    <Waypoint onEnter={() => handleEnter()} onLeave={() => handleExit()} bottomOffset={'70%'}>
      <div
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        className={`c-footer ${getModuleBackgroundColor('blue')}`}
      >
        <div className="c-footer__inner--smaller">
          <div className="c-footer__row desktop-margin-bottom">
            <NewsletterSignUp title={newsletterSignupTitle} />
            <div className="c-footer__menu-lists">
              <div className="c-footer__menu-list-group">
                <div className="c-footer__company">
                  <FooterMenuList title={companyMenuTitle} list={companyMenuList} />
                </div>
                <div className="c-footer__resellers">
                  <FooterMenuList title={resellersMenuTitle} list={resellersMenuList} />
                </div>
              </div>
              <div className="c-footer__menu-list-group">
                <div className="c-footer__contact">
                  <FooterContactList
                    title={contactMenuTitle}
                    contactMenuGroup1={contactMenuGroup1}
                    contactMenuGroup2={contactMenuGroup2}
                    contactMenuGroup3={contactMenuGroup3}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="c-footer__inner--wider">
          <div className="c-footer__row">
            <div className="c-footer__social-list">
              <FooterSocialList socialLinks={socialLinks} />
            </div>
          </div>
          <div className="c-footer__row mobile-reverse">
            <div className="c-footer__legal-container">
              <FooterLegalList legalLinks={legalLinks} />
            </div>
            <div className="c-footer__select-container">
              <Select
                onChange={onChange}
                data={countriesData}
                type={'transparent'}
                label={'Select country'}
              />
            </div>
          </div>
        </div>
        <div className="c-footer__owl-container">
          <FooterOwl
            rotateLeftEye={rotateLeftEye}
            rotateRightEye={rotateRightEye}
            isHovering={isHovering}
          />
        </div>
      </div>
    </Waypoint>
  );
};

export default Footer;
